import { useState } from "react";
import { Button, Group, Modal, NativeSelect, Stack, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { api } from "@/lib/api";

type Scope = "general" | "project" | "workspace";
type Snapshot = {
  scope: Scope;
  project_id?: string;
  material_type?: string;
  confirmation_token: string;
  count: number;
  scopeLabel: string;
  blockLabel: string;
};

export function KnowledgeDeleteControls({
  projectId,
  projectName,
  types,
  onDeleted,
}: {
  projectId: string | null;
  projectName: string;
  types: Array<{ key: string; label: string }>;
  onDeleted: () => Promise<void>;
}) {
  const [area, setArea] = useState("section");
  const [kind, setKind] = useState("");
  const [busy, setBusy] = useState(false);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const report = (error: unknown) =>
    showNotification({
      title: "Удаление не выполнено",
      message:
        error instanceof Error ? error.message : "Не удалось удалить материалы",
      color: "red",
    });
  const preview = async () => {
    setBusy(true);
    try {
      const scope: Scope =
        area === "workspace" ? "workspace" : projectId ? "project" : "general";
      const body = {
        scope,
        project_id: scope === "project" ? projectId! : undefined,
        material_type: kind || undefined,
      };
      const result = await api.supportKnowledgeBulkDelete(body, true);
      setSnapshot({
        ...body,
        ...result,
        scopeLabel:
          scope === "workspace"
            ? "Вся база знаний текущего рабочего пространства"
            : projectName,
        blockLabel: types.find((t) => t.key === kind)?.label || "Все материалы",
      });
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!snapshot || busy) return;
    setBusy(true);
    try {
      const result = await api.supportKnowledgeBulkDelete(snapshot);
      setSnapshot(null);
      await onDeleted();
      showNotification({
        title: "Материалы удалены",
        message: `Удалено: ${result.deleted}`,
        color: "green",
      });
    } catch (error) {
      setSnapshot(null);
      report(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Group align="flex-end">
        <NativeSelect
          label="Область удаления"
          value={area}
          onChange={(e) => setArea(e.currentTarget.value)}
          data={[
            { value: "section", label: `Раздел: ${projectName}` },
            { value: "workspace", label: "Вся БЗ — все проекты и Общие" },
          ]}
        />
        <NativeSelect
          label="Блок для удаления"
          value={kind}
          onChange={(e) => setKind(e.currentTarget.value)}
          data={[
            { value: "", label: "Все материалы" },
            ...types.map((t) => ({ value: t.key, label: t.label })),
          ]}
        />
        <Button
          color="red"
          variant="light"
          loading={busy}
          onClick={() => void preview()}
        >
          Удалить материалы…
        </Button>
      </Group>
      <Modal
        opened={!!snapshot}
        onClose={() => {
          if (!busy) setSnapshot(null);
        }}
        title="Подтверждение удаления"
        closeOnClickOutside={!busy}
        closeOnEscape={!busy}
        withCloseButton={!busy}
      >
        {snapshot && (
          <Stack>
            <Text>Область: «{snapshot.scopeLabel}».</Text>
            <Text>Блок: «{snapshot.blockLabel}».</Text>
            <Text>
              Будет удалено материалов: {snapshot.count} (включая неактивные).
            </Text>
            <Text c="red">
              {snapshot.scope === "workspace"
                ? "Будут затронуты все проекты и раздел «Общие» текущего рабочего пространства."
                : "Другие проекты и разделы не будут затронуты."}{" "}
              {snapshot.material_type
                ? "Остальные блоки не будут затронуты."
                : ""}{" "}
              Удаление необратимо.
            </Text>
            <Group justify="flex-end">
              <Button
                variant="default"
                disabled={busy}
                onClick={() => setSnapshot(null)}
              >
                Отмена
              </Button>
              <Button
                color="red"
                loading={busy}
                disabled={!snapshot.count}
                onClick={() => void remove()}
              >
                Удалить
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
